import { useState, useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toasterApi } from '@terra/ui-design-system';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import { terraProxyApiClient } from '../clients/testerClient';
import {
  APPLICATION_MEMORY_OVERLAYS_QUERY_KEY,
  createPendingMemoryOverlay,
  createRejectedMemoryOverlay,
  isSyntheticMemoryRecordId,
  toMemorySnapshot,
  writePersistedApplicationMemoryOverlays,
  type ApplicationMemoryOverlay,
} from './applicationMemoryOverlays';

const setApplicationMemoryOverlays = ({
  queryClient,
  applicationId,
  updater,
}: {
  queryClient: QueryClient;
  applicationId: string;
  updater: (current: ApplicationMemoryOverlay[]) => ApplicationMemoryOverlay[];
}) => {
  const overlaysQueryKey = [
    APPLICATION_MEMORY_OVERLAYS_QUERY_KEY,
    applicationId,
  ] as const;
  const nextOverlays = updater(
    queryClient.getQueryData<ApplicationMemoryOverlay[]>(overlaysQueryKey) ??
      [],
  );

  queryClient.setQueryData(overlaysQueryKey, nextOverlays);
  writePersistedApplicationMemoryOverlays({
    applicationId,
    overlays: nextOverlays,
  });
};

type UseMemoryActionsParams = {
  applicationId: string | undefined;
  onSuccess?: () => void;
};

type ActionState = {
  isSubmitting: boolean;
  actionInProgress: string | null;
};

type UseMemoryActionsReturn = {
  actionState: ActionState;
  createMemory: (
    content: string,
    options?: { replaceRecordId?: string },
  ) => Promise<boolean>;
  updateMemory: (recordId: string, content: string) => Promise<boolean>;
  deleteMemory: (memory: MemoryRecordResponse) => Promise<boolean>;
  isActionInProgress: (recordId: string) => boolean;
};

export function useMemoryActions({
  applicationId,
  onSuccess,
}: UseMemoryActionsParams): UseMemoryActionsReturn {
  const queryClient = useQueryClient();

  const [actionState, setActionState] = useState<ActionState>({
    isSubmitting: false,
    actionInProgress: null,
  });

  const invalidateAndRefetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['memories'] });
    onSuccess?.();
  }, [queryClient, onSuccess]);

  const createMemory = useCallback(
    async (
      content: string,
      { replaceRecordId }: { replaceRecordId?: string } = {},
    ): Promise<boolean> => {
      if (!applicationId || !content.trim()) return false;

      setActionState({
        isSubmitting: true,
        actionInProgress: replaceRecordId ?? 'create',
      });

      try {
        const result = await terraProxyApiClient.createMemoryRecord(
          applicationId,
          content,
        );

        if (!result.sessionId && !result.recordId) {
          throw new Error('Create succeeded without sessionId or recordId');
        }

        if (!result.recordId && result.sessionId) {
          const memoriesData = await queryClient
            .ensureQueryData({
              queryKey: ['memories', applicationId],
              queryFn: () =>
                terraProxyApiClient.getMemoriesByApplication(applicationId),
            })
            .catch(() => undefined);

          const overlay = createPendingMemoryOverlay({
            sessionId: result.sessionId,
            content: content.trim(),
            beforeSnapshot: toMemorySnapshot(memoriesData?.records ?? []),
          });

          setApplicationMemoryOverlays({
            queryClient,
            applicationId,
            updater: (current) => [
              overlay,
              ...current.filter((item) => item.recordId !== replaceRecordId),
            ],
          });
        } else if (replaceRecordId) {
          setApplicationMemoryOverlays({
            queryClient,
            applicationId,
            updater: (current) =>
              current.filter((item) => item.recordId !== replaceRecordId),
          });
        }

        toasterApi.success({
          title: 'Memory saved',
          description: 'Your memory has been submitted.',
        });

        await invalidateAndRefetch();

        return true;
      } catch (error) {
        const rejectionReason =
          error instanceof Error
            ? error.message
            : 'An error occurred while submitting the memory';

        if (
          applicationId &&
          rejectionReason.toLowerCase().includes('rejected')
        ) {
          const overlay = createRejectedMemoryOverlay({
            content: content.trim(),
            rejectionReason,
          });

          setApplicationMemoryOverlays({
            queryClient,
            applicationId,
            updater: (current) => [
              overlay,
              ...current.filter((item) => item.recordId !== replaceRecordId),
            ],
          });
        }

        console.error('Failed to submit memory:', error);
        toasterApi.error({
          title: 'Failed to submit memory',
          description: rejectionReason,
        });

        return false;
      } finally {
        setActionState({ isSubmitting: false, actionInProgress: null });
      }
    },
    [applicationId, invalidateAndRefetch, queryClient],
  );

  const updateMemory = useCallback(
    async (recordId: string, content: string): Promise<boolean> => {
      if (!applicationId || !content.trim()) return false;

      if (isSyntheticMemoryRecordId(recordId)) {
        return createMemory(content, { replaceRecordId: recordId });
      }

      setActionState({ isSubmitting: true, actionInProgress: recordId });

      try {
        const sessionId = `memory-correction-${recordId}-${Date.now()}`;

        await terraProxyApiClient.updateMemoryRecord(
          applicationId,
          recordId,
          content.trim(),
          sessionId,
        );

        const memoriesData = await queryClient
          .ensureQueryData({
            queryKey: ['memories', applicationId],
            queryFn: () =>
              terraProxyApiClient.getMemoriesByApplication(applicationId),
          })
          .catch(() => undefined);

        const overlay = createPendingMemoryOverlay({
          sessionId,
          content: content.trim(),
          beforeSnapshot: toMemorySnapshot(memoriesData?.records ?? []),
          replacesRecordId: recordId,
        });

        setApplicationMemoryOverlays({
          queryClient,
          applicationId,
          updater: (current) => [
            overlay,
            ...current.filter(
              (item) =>
                item.recordId !== recordId &&
                item.replacesRecordId !== recordId,
            ),
          ],
        });

        toasterApi.success({
          title: 'Memory updated',
          description: 'A correction event has been created for this memory.',
        });

        await invalidateAndRefetch();

        return true;
      } catch (error) {
        console.error('Failed to update memory:', error);
        toasterApi.error({
          title: 'Failed to update memory',
          description:
            error instanceof Error
              ? error.message
              : 'An error occurred while updating the memory',
        });

        return false;
      } finally {
        setActionState({ isSubmitting: false, actionInProgress: null });
      }
    },
    [applicationId, createMemory, invalidateAndRefetch, queryClient],
  );

  const deleteMemory = useCallback(
    async (memory: MemoryRecordResponse): Promise<boolean> => {
      if (!applicationId) return false;

      setActionState({ isSubmitting: true, actionInProgress: memory.recordId });

      try {
        if (isSyntheticMemoryRecordId(memory.recordId)) {
          setApplicationMemoryOverlays({
            queryClient,
            applicationId,
            updater: (current) =>
              current.filter((overlay) => overlay.recordId !== memory.recordId),
          });
          toasterApi.success({
            title: 'Memory deleted',
            description: 'The memory has been permanently deleted.',
          });

          return true;
        }

        await terraProxyApiClient.deleteMemoryRecord(
          applicationId,
          memory.recordId,
        );

        toasterApi.success({
          title: 'Memory deleted',
          description: 'The memory has been permanently deleted.',
        });

        await invalidateAndRefetch();

        return true;
      } catch (error) {
        console.error('Failed to delete memory:', error);
        toasterApi.error({
          title: 'Failed to delete memory',
          description:
            error instanceof Error
              ? error.message
              : 'An error occurred while deleting the memory',
        });

        return false;
      } finally {
        setActionState({ isSubmitting: false, actionInProgress: null });
      }
    },
    [applicationId, invalidateAndRefetch, queryClient],
  );

  const isActionInProgress = useCallback(
    (recordId: string) => actionState.actionInProgress === recordId,
    [actionState.actionInProgress],
  );

  return {
    actionState,
    createMemory,
    updateMemory,
    deleteMemory,
    isActionInProgress,
  };
}
